import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

const Section = ({ title, children }) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
    <h2 className="font-semibold" style={{ fontSize: '1.125rem' }}>{title}</h2>
    <div className="text-muted" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', lineHeight: '1.6' }}>
      {children}
    </div>
  </section>
)

const PrivacyPolicy = () => {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)', padding: '3rem 1rem' }}>
      <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
        {/* Back link */}
        <Link
          to="/login"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--text-soft)',
            textDecoration: 'none',
            marginBottom: '2rem',
            transition: 'color 0.15s'
          }}
        >
          <ArrowLeft style={{ width: '1rem', height: '1rem' }} />
          Back to login
        </Link>

        {/* Header */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h1 className="font-bold" style={{ fontSize: '1.875rem', marginBottom: '0.5rem' }}>
            Privacy Policy
          </h1>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>Last updated: 22 March 2026</p>
        </div>

        <div
          className="card"
          style={{
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}
        >
          <Section title="1. Who We Are">
            <p>
              WebNest ("we", "us", "our") is a web design and content management service operated by
              Marco Alcini. We provide website building, hosting, and management tools to small businesses.
            </p>
            <p>
              Contact us at:{' '}
              <a href="mailto:hello@marcoalcini.com" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                hello@marcoalcini.com
              </a>
            </p>
          </Section>

          <Section title="2. What Data We Collect">
            <p>When you use WebNest, we collect:</p>
            <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <li><strong>Account data</strong> — your name, email address, and password (stored as an encrypted hash)</li>
              <li><strong>Company data</strong> — business name, contact details, and industry</li>
              <li><strong>Website content</strong> — text, images, and files you upload to build your website</li>
              <li><strong>Billing data</strong> — subscription plan and payment history. Card details are processed directly by Stripe and never stored on our servers</li>
              <li><strong>Usage data</strong> — login timestamps and activity within the platform</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>We use your data to:</p>
            <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <li>Provide and manage your WebNest account and website</li>
              <li>Process subscription payments via Stripe</li>
              <li>Send service notifications and updates by email</li>
              <li>Respond to support requests</li>
              <li>Maintain the security and performance of the platform</li>
            </ul>
            <p>We do not sell your data to third parties. We do not use your data for advertising.</p>
          </Section>

          <Section title="4. Third-Party Services">
            <p>We use the following trusted third-party services to operate the platform:</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                fontSize: '0.875rem',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                overflow: 'hidden',
                marginTop: '0.5rem',
                borderCollapse: 'collapse'
              }}>
                <thead style={{ background: 'var(--surface-2)' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem', fontWeight: 500 }}>Service</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem', fontWeight: 500 }}>Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { service: 'Microsoft Azure', purpose: 'Cloud hosting and file storage' },
                    { service: 'Stripe', purpose: 'Payment processing' },
                    { service: 'Resend', purpose: 'Transactional email delivery' }
                  ].map(({ service, purpose }) => (
                    <tr key={service} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '0.5rem 1rem', fontWeight: 500 }}>{service}</td>
                      <td style={{ padding: '0.5rem 1rem' }}>{purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>
              Each provider is contractually bound to handle your data securely and in compliance with UK GDPR.
            </p>
          </Section>

          <Section title="5. Cookies">
            <p>
              WebNest uses only essential, functional browser storage necessary to keep you logged in and
              remember your preferences. We do not use tracking, analytics, or advertising cookies.
              No cookie consent is required for essential cookies under UK GDPR.
            </p>
          </Section>

          <Section title="6. Data Storage and Security">
            <p>
              Your data is stored on Microsoft Azure servers. We implement appropriate technical measures
              including encrypted passwords, secure HTTPS connections, and access controls to protect your data.
            </p>
          </Section>

          <Section title="7. Data Retention">
            <p>
              We retain your data for as long as your account is active. If you close your account, your
              personal data will be deleted within 30 days, except where we are required to retain it for
              legal or financial record-keeping purposes (e.g. billing records, up to 7 years under UK law).
            </p>
          </Section>

          <Section title="8. Your Rights Under UK GDPR">
            <p>You have the right to:</p>
            <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <li><strong>Access</strong> the personal data we hold about you</li>
              <li><strong>Correct</strong> inaccurate data</li>
              <li><strong>Delete</strong> your data ("right to be forgotten")</li>
              <li><strong>Restrict</strong> or <strong>object</strong> to processing</li>
              <li><strong>Data portability</strong> — receive your data in a portable format</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:hello@marcoalcini.com" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                hello@marcoalcini.com
              </a>
              . We will respond within 30 days.
            </p>
            <p>
              You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                ico.org.uk
              </a>
              .
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this policy from time to time. We will notify you by email of any significant
              changes. Continued use of the service after changes constitutes acceptance.
            </p>
          </Section>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '2rem',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--text-soft)',
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem'
        }}>
          <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.15s' }}>
            Terms &amp; Conditions
          </Link>
          <span>·</span>
          <Link to="/login" style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.15s' }}>
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
